import { useEffect, useMemo, useRef, useState } from "react";
import Hls from "hls.js";
import { Link } from "react-router-dom";
import { Volume2, VolumeX, ThumbsUp, MessageCircle, Share2, Flag, Check } from "lucide-react";
import { api, getToken } from "../api";
import { useAuth, useGuard } from "../auth";
import { mediaUrl, fmtNum } from "../config";
import { Avatar, Spinner, Empty } from "../components/cards";
import CommentsSheet from "../components/CommentsSheet";
import { orderFeed } from "../subscriptions";

const ShortItem = ({ video, active }) => {
  const ref = useRef(null);
  const guard = useGuard();
  const [muted, setMuted] = useState(true);
  const [liked, setLiked] = useState(!!video.liked);
  const [likes, setLikes] = useState(video.likes ?? 0);
  const [commentCount, setCommentCount] = useState(video.commentCount ?? video.comments ?? 0);
  const [showComments, setShowComments] = useState(false);
  const [shared, setShared] = useState(false);
  const [reported, setReported] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const src = mediaUrl(video.webVideoUrl || video.videoUrl);
    const isHls = video.streamType === "hls" || src.includes(".m3u8");
    let hls;
    if (isHls && !el.canPlayType("application/vnd.apple.mpegurl") && Hls.isSupported()) {
      hls = new Hls();
      hls.loadSource(src);
      hls.attachMedia(el);
    } else {
      el.src = src;
    }
    return () => {
      if (hls) hls.destroy();
      el.pause();
    };
  }, [video]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (active) {
      el.play().catch(() => {});
      if (getToken()) api.post(`/videos/${video.id}/view`).catch(() => {});
    } else {
      el.pause();
      el.currentTime = 0;
    }
  }, [active, video.id]);

  const toggleLike = async () => {
    if (!guard()) return;
    try {
      const r = await api.post(`/videos/${video.id}/like`);
      setLiked(r.liked ?? !liked);
      if (r.likes != null) setLikes(r.likes);
    } catch {}
  };

  const openComments = () => {
    if (!guard()) return;
    setShowComments(true);
  };

  const share = async () => {
    if (!guard()) return;
    const url = `${window.location.origin}/app/watch/${video.id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: video.title, url });
      } else {
        try {
          await navigator.clipboard.writeText(url);
        } catch {
          const ta = document.createElement("textarea");
          ta.value = url;
          ta.style.position = "fixed";
          ta.style.opacity = "0";
          document.body.appendChild(ta);
          ta.select();
          try { document.execCommand("copy"); } catch {}
          document.body.removeChild(ta);
        }
        setShared(true);
        setTimeout(() => setShared(false), 2000);
      }
    } catch {}
  };

  const report = async () => {
    if (!guard() || reported) return;
    try {
      await api.post("/reports", { kind: "video", targetId: video.id, ref: video.id, reason: "Reported from the web app" });
    } catch {}
    setReported(true);
  };

  return (
    <div className="relative flex h-full w-full snap-start items-center justify-center" data-testid={`short-${video.id}`}>
      <video
        ref={ref}
        loop
        muted={muted}
        playsInline
        poster={mediaUrl(video.thumbnail) || undefined}
        className="h-full max-h-full w-auto max-w-full rounded-xl object-contain"
        onClick={() => setMuted((m) => !m)}
      />
      <div className="absolute bottom-6 left-1/2 w-full max-w-md -translate-x-1/2 px-4">
        <div className="flex items-end justify-between gap-4">
          <div className="min-w-0">
            <Link to={`/app/creator/${video.creatorId}`} className="flex items-center gap-2">
              <Avatar user={video.creator} size={32} />
              <span className="font-heading text-sm font-semibold text-white">{video.creator?.displayName || video.creator?.username}</span>
            </Link>
            <p className="mt-2 line-clamp-2 text-sm text-slate-200">{video.title}</p>
          </div>
          <div className="flex flex-col items-center gap-4">
            <button onClick={toggleLike} className={`flex flex-col items-center gap-1 ${liked ? "text-crimson" : "text-white"}`} data-testid={`short-like-${video.id}`}>
              <ThumbsUp size={22} fill={liked ? "currentColor" : "none"} />
              <span className="font-mono text-[10px]">{fmtNum(likes)}</span>
            </button>
            <button onClick={openComments} className="flex flex-col items-center gap-1 text-white" data-testid={`short-comments-${video.id}`}>
              <MessageCircle size={22} />
              {commentCount > 0 && <span className="font-mono text-[10px]">{fmtNum(commentCount)}</span>}
            </button>
            <button onClick={share} className="flex flex-col items-center gap-1 text-white" data-testid={`short-share-${video.id}`}>
              {shared ? <Check size={22} className="text-emerald-400" /> : <Share2 size={22} />}
              <span className="font-mono text-[10px]">{shared ? "Copied" : "Share"}</span>
            </button>
            <button onClick={report} className={`flex flex-col items-center gap-1 ${reported ? "text-crimson" : "text-white"}`} data-testid={`short-report-${video.id}`}>
              <Flag size={22} fill={reported ? "currentColor" : "none"} />
              <span className="font-mono text-[10px]">{reported ? "Reported" : "Report"}</span>
            </button>
            <button onClick={() => setMuted((m) => !m)} className="text-white" data-testid={`short-mute-${video.id}`}>
              {muted ? <VolumeX size={22} /> : <Volume2 size={22} />}
            </button>
          </div>
        </div>
      </div>
      {showComments && (
        <CommentsSheet contentId={video.id} title={video.title} onClose={() => setShowComments(false)} />
      )}
    </div>
  );
};

const Shorts = () => {
  const { user } = useAuth();
  const [shorts, setShorts] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    api.get("/videos?format=short&limit=60").then((r) => {
      const list = Array.isArray(r) ? r : r.videos || [];
      setShorts(list);
    }).catch(() => setShorts([]));
  }, []);

  // Recommendation ordering: shorts from subscribed creators first (newest first),
  // then a shuffled mix; signed-out visitors get a fresh random mix each refresh.
  const ordered = useMemo(() => {
    if (!shorts) return null;
    const feed = orderFeed(shorts, user);
    return [...(feed.fromSubs || []), ...(feed.rest || [])];
  }, [shorts, user]);

  useEffect(() => {
    if (ordered && ordered[0]) setActiveId(ordered[0].id);
  }, [ordered]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !ordered?.length) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) setActiveId(en.target.dataset.id);
        });
      },
      { root: container, threshold: 0.6 }
    );
    container.querySelectorAll("[data-id]").forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [ordered]);

  if (ordered === null) return <Spinner />;
  if (ordered.length === 0) return <Empty>No shorts yet.</Empty>;

  return (
    <div
      ref={containerRef}
      className="h-[calc(100dvh-8rem)] snap-y snap-mandatory overflow-y-scroll rounded-2xl lg:h-[calc(100dvh-7rem)]"
      data-testid="shorts-feed"
    >
      {ordered.map((v) => (
        <div key={v.id} data-id={v.id} className="h-full snap-start">
          <ShortItem video={v} active={activeId === v.id} />
        </div>
      ))}
    </div>
  );
};

export default Shorts;
